import { useMemo } from 'react';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useTopicMenu } from 'hb-react/shared/apps/Topics/hooks/useTopicMenu';

import type { MenuFeatures, TopLevelMenu, MenuItem } from './types';

const check = (item: any, value: string) =>
  item?.identifier === value || item?.feature_name === value;

export const getMenuByFeatureId = (
  pages?: Array<TopLevelMenu | MenuItem>,
  id?: string,
) => {
  if (!Array.isArray(pages) || pages.length === 0 || !id) {
    return null;
  }

  const menu = pages.reduce((result, page) => {
    // Submenus contains all fields in it's object directly.
    if (check(page, id)) {
      return { ...page };
    }

    // Menu of top level will have all menu fields in menu object.
    if ('menu' in page && check(page.menu, id)) {
      return { ...page.menu };
    }

    // ...and submenus in submenus list.
    if ('submenus' in page) {
      const submenuFound = getMenuByFeatureId(page.submenus, id);

      if (submenuFound) {
        return { ...submenuFound };
      }
    }

    return result;
  }, null);

  return menu;
};

/**
 * Returns the name of the feature in the topic menu or network menu depending
 * on the context it's used.
 *
 * @param featureIdentifier Identifier of the feature
 * @param defaultValue Default value (optional)
 * @returns the name of the feature in the menu
 */
export const useMenuName = <T extends keyof MenuFeatures>(
  featureIdentifier: T,
  defaultValue: string = '',
) => {
  const isTopicFeature = featureIdentifier === 'topics';

  // If not in topic context, will return default value of the context
  const { topicId, topic = {} } = useTopicContext();
  const { appPages: topicPages, isLoading } = useTopicMenu({
    // Topic object might be not defined yet.
    topic: { id: topicId, ...topic },
    enabled: !!topicId && !isTopicFeature,
  });

  const { currentNetwork } = useDefaultAppContexts();

  return useMemo(() => {
    if (topicId && !isTopicFeature) {
      if (isLoading) {
        return null;
      }

      const topicMenu = getMenuByFeatureId(topicPages, featureIdentifier);

      if (topicMenu) {
        return topicMenu.menu_name;
      }
      // Fallback to the network menu if the feature is not found.
    }

    const networkMenu = getMenuByFeatureId(
      currentNetwork.app_pages as TopLevelMenu[],
      featureIdentifier,
    );

    return networkMenu?.menu_name || defaultValue;
  }, [
    topicId,
    isTopicFeature,
    featureIdentifier,
    currentNetwork.app_pages,
    defaultValue,
    isLoading,
    topicPages,
  ]);
};
